import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {DocumentResponse} from "@mytour/shared";
import {useState} from "react";
import {toast} from "react-toastify";
import {Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {useDeleteDocument} from "../../query/documentQueries";

type Props = {
  doc: DocumentResponse;
  label?: string;
};

/**
 * Common delete button + confirm modal used by all card types. Soft-delete at the API
 * layer means an admin can recover from accidents — the modal is still here to nudge users
 * away from misclicks. Errors stay inline in the modal per Action Component Pattern 2;
 * success surfaces a toast after the modal closes.
 */
export function DocumentDeleteAction({doc, label}: Props) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {deleteDocument, isDeletingDocument} = useDeleteDocument();

  return (
    <>
      <Button color="danger" onClick={() => setOpen(true)}>
        <FontAwesomeIcon icon="times" />
        {label && <>&nbsp;{label}</>}
      </Button>
      <Modal isOpen={open} toggle={handleClose} centered>
        <ModalHeader toggle={handleClose}>Supprimer ce document ?</ModalHeader>
        <ModalBody>
          L'élément sera supprimé du dossier client.
          {error && (
            <Alert color="danger" className="mt-2 mb-0">
              {error}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isDeletingDocument}>
            Annuler
          </Button>
          <Button color="danger" onClick={handleConfirm} disabled={isDeletingDocument}>
            Supprimer
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );

  function handleClose() {
    if (!isDeletingDocument) {
      setOpen(false);
      setError(null);
    }
  }

  async function handleConfirm() {
    try {
      setError(null);
      await deleteDocument(doc);
      setOpen(false);
      toast.success("Document supprimé", {autoClose: 2200});
    } catch {
      setError("Erreur lors de la suppression");
    }
  }
}
